import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Button } from '../../../shared/components/ui/button';
import { Card } from '../../../shared/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../shared/components/ui/tabs';
import { DashboardStats } from './DashboardStats';
import { MyClientsSection } from './components/MyClientsSection';
import { ProfileTabContent, UNSAVED_CHANGES_PROMPT } from './components/ProfileTabContent';
import { useProviderStats } from './hooks/useProviderStats';
import { useProviderProfile } from './hooks/useProviderProfile';
import { EngagementsDashboard } from '../../consultations/components/EngagementsDashboard';
import { InviteClientModal } from '../../sp-client-management/components/InviteClientModal';
import { PermissionGate } from '@/features/workspace-sub-delegation';
import {
  PermissionDeniedCard,
  isWorkspacePermissionError,
} from '@/shared/components/sub-delegation';

/**
 * Default account status used when profile data is unavailable
 * This represents a newly created account pending verification
 */
const DEFAULT_ACCOUNT_STATUS = 'PENDING_VERIFICATION';

/**
 * Valid tab values for the Service Provider Dashboard
 * Feature 042: Removed 'settings' tab - invalid values redirect to 'overview'
 */
const VALID_TABS = ['overview', 'leads', 'profile'] as const;
type Tab = (typeof VALID_TABS)[number];

export function Dashboard() {
  const navigate = useNavigate();
  // Feature 102: emails deep-link to a tab (e.g. /dashboard?tab=leads from
  // the call-request lead email). Invalid values fall back to overview.
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<Tab>(
    requestedTab && (VALID_TABS as readonly string[]).includes(requestedTab)
      ? (requestedTab as Tab)
      : 'overview',
  );
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isProfileFormDirty, setIsProfileFormDirty] = useState(false);

  const isValidTab = (value: string): value is Tab => {
    return (VALID_TABS as readonly string[]).includes(value);
  };

  /**
   * Handle tab changes with validation
   * Redirects invalid tab values (e.g., removed 'settings' tab) to 'overview'
   * Feature 042: Prevents broken navigation to removed Settings tab
   */
  const handleTabChange = (value: string) => {
    // Leaving the Profile tab unmounts the profile form; confirm before discarding edits.
    if (
      activeTab === 'profile' &&
      value !== 'profile' &&
      isProfileFormDirty &&
      !window.confirm(UNSAVED_CHANGES_PROMPT)
    ) {
      return;
    }
    if (isValidTab(value)) {
      setActiveTab(value);
    } else {
      // Invalid tab value (e.g., from bookmarked URL or cached link) - redirect to overview
      if (!import.meta.env.PROD) {
        console.warn(`Invalid tab value "${value}" - redirecting to overview`);
      }
      setActiveTab('overview');
    }
  };

  // Fetch data using hooks
  const { data: stats, isLoading: isLoadingStats, error: statsError } = useProviderStats();
  const {
    data: profile,
    onboardingStatus,
    isLoading: isLoadingProfile,
    error: profileError,
  } = useProviderProfile();

  const isLoading = isLoadingStats || isLoadingProfile;
  const hasError = statsError || profileError;

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    // Backstop: a delegate hitting a workspace-permission 403 (e.g. a race
    // after a mid-session permission change) sees a graceful denied card
    // instead of the raw error text.
    if (isWorkspacePermissionError(statsError) || isWorkspacePermissionError(profileError)) {
      return <PermissionDeniedCard feature="this dashboard" variant="page" />;
    }

    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Error loading dashboard data</p>
          <p className="text-sm text-text-secondary mt-2">
            {statsError?.message || profileError?.message}
          </p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const { isComplete: isOnboardingComplete } = onboardingStatus;

  return (
    <div className="min-h-screen bg-background p-8 space-y-8">
      <div>
        <h1 className="text-3xl text-text-primary">Service Provider Dashboard</h1>
        <p className="text-text-secondary mt-2">Manage your profile and track your performance</p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="mb-8">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="leads">Consultations</TabsTrigger>
          <TabsTrigger value="profile">Profile</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Key Metrics - Positioned at top for immediate visibility */}
          <div>
            <h2 className="text-xl text-text-primary mb-4">Key Metrics</h2>
            <DashboardStats stats={stats} isOnboardingComplete={isOnboardingComplete} />
          </div>

          {/* My Clients Section - Includes referral link, active clients, and pending invitations */}
          <PermissionGate permission="WORKSPACE_OPERATIONS" feature="clients">
            <MyClientsSection
              providerId={profile?.userId || ''}
              onInviteClick={() => setIsInviteModalOpen(true)}
              onMessageClick={(clientId) => {
                navigate(`/messages?recipient=${encodeURIComponent(clientId)}`);
              }}
            />
          </PermissionGate>

          {/* Marketing Resources - Feature 044 */}
          <Card className="p-6">
            <h3 className="text-lg text-text-primary mb-4">
              Service Provider Resources
            </h3>
            <p className="text-sm text-text-secondary mb-6">
              Check out your Service Provider Resource Page, it includes helpful assets like our Speaker Interest Form, Referral Form, and our Content Submission Form. It also includes valuable information on how to follow us on Social Media and tips for getting the most out of your membership.
            </p>
            <Button
              onClick={() => {
                const newWindow = window.open(
                  'https://marketing.thebquest.com/provider-resources',
                  '_blank',
                  'noopener,noreferrer'
                );
                if (newWindow) {
                  newWindow.opener = null;
                }
              }}
              className="bg-bquest-green hover:bg-bquest-green/90 gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Access Resources
            </Button>
          </Card>
        </TabsContent>

        {/* Engagements Tab - Feature 014 */}
        <TabsContent value="leads">
          <PermissionGate permission="WORKSPACE_OPERATIONS" feature="consultations">
            <EngagementsDashboard />
          </PermissionGate>
        </TabsContent>

        {/* Profile Tab - Feature 022 */}
        <TabsContent value="profile">
          <ProfileTabContent
            profile={profile}
            isOnboardingComplete={isOnboardingComplete}
            accountStatus={profile?.user?.accountStatus || DEFAULT_ACCOUNT_STATUS}
            vettingStatus={profile?.vettingStatus || 'PENDING'}
            onDirtyChange={setIsProfileFormDirty}
          />
        </TabsContent>
      </Tabs>

      {/* Invite Client Modal */}
      <InviteClientModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        providerId={profile?.userId || ''}
        onSuccess={() => {
          // Modal will auto-close on success
        }}
      />
    </div>
  );
}

